import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal } from "../lib/motion";
import { CTASection } from "../components/site/CTASection";

const PRINCIPLES = [
  { t: "Architecture before decoration", d: "We resolve space, proportion and light before considering surface and ornament." },
  { t: "Respect for context", d: "Existing character is studied and understood rather than erased." },
  { t: "Natural materiality", d: "We favour tactile, authentic materials selected for how they feel, perform and age with use." },
  { t: "Emotional longevity", d: "We design for how a home will feel in ten years, not only on completion." },
  { t: "Thoughtful collaboration", d: "We work openly with clients, consultants and makers throughout." },
  { t: "Precision in the details", d: "The quality of a junction or a shadow gap is never incidental." },
];

const VALUES = [
  "Clarity over complication",
  "Craft over trend",
  "Restraint over excess",
  "Warmth over spectacle",
];

export default function Studio() {
  return (
    <>
      <Seo
        title="Studio | Arc & Hollow — London Interior Architecture"
        description="Arc & Hollow is a London interior architecture and design studio with a thoughtful, personal and technically informed approach to residential interiors."
        path="/studio"
      />
      <PageHero
        overline="The Studio"
        titleLines={["A studio of", "considered homes."]}
        intro="Arc & Hollow is an independent London interior architecture and design studio founded on the belief that the most meaningful homes are shaped through the relationship between what is visible and what is quietly working beneath the surface."
      />

      <section className="container-x pb-24 md:pb-32">
        <Reveal>
          <div className="grid gap-x-16 gap-y-8 md:grid-cols-2">
            <p className="text-lg font-light leading-relaxed text-olive">
              Each project is approached as an architectural, material and emotional composition.
              Existing character is studied rather than erased; new interventions are introduced with
              clarity; and furniture, art and objects are considered as integral parts of the whole.
            </p>
            <p className="text-lg font-light leading-relaxed text-olive">
              The studio works with a limited number of clients at any one time, bringing close
              attention, creative focus and technical rigour to every commission.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-charcoal text-chalk py-24 md:py-32" data-testid="principles">
        <div className="container-x">
          <Reveal><p className="overline text-bronze">Design principles</p></Reveal>
          <div className="mt-14 grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 0.08} className="border-t border-chalk/20 pt-6">
                <h3 className="font-serif text-2xl font-light">{p.t}</h3>
                <p className="mt-3 text-sm text-chalk/60 font-light leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-24 md:py-32" data-testid="practice">
        <div className="grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="overline text-bronze">Approach</p>
            <h2 className="mt-5 font-serif text-3xl md:text-4xl font-light leading-tight">
              A focused, technically informed practice
            </h2>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-lg font-light leading-relaxed text-olive">
                The studio's work combines creative direction with detailed development and project
                coordination. Spatial planning, lighting, materials, bespoke joinery and FF&amp;E are
                considered together so that the design remains coherent from the earliest concept
                through to installation.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="mt-6 text-lg font-light leading-relaxed text-olive">
                Arc &amp; Hollow collaborates with trusted architects, engineers, contractors,
                craftspeople and specialist consultants according to the requirements of each project.
                This collaborative approach allows every commission to receive the appropriate technical
                expertise while retaining a clear and consistent design vision.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-limestone/40 py-24 md:py-32" data-testid="values">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="overline text-bronze">Studio values</p>
            <h2 className="mt-5 font-serif text-3xl md:text-4xl font-light">What guides the work</h2>
          </Reveal>
          <div className="lg:col-span-8">
            {VALUES.map((v, i) => (
              <Reveal key={v} delay={i * 0.06}>
                <div className="flex items-center gap-6 border-t border-charcoal/15 py-6">
                  <span className="overline text-bronze">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-2xl md:text-3xl font-light">{v}</span>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-charcoal/15" />
          </div>
        </div>
      </section>

      <section className="container-x py-24 md:py-32" data-testid="collaborators">
        <div className="max-w-3xl">
          <Reveal><p className="overline text-bronze">Collaborators &amp; makers</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-3xl md:text-4xl font-light leading-tight">
              A trusted circle of craftspeople and consultants
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg font-light leading-relaxed text-olive">
              We work alongside skilled joiners, stonemasons, metalworkers, lighting specialists,
              architects, engineers and contractors whose expertise gives the design physical
              substance. The appropriate professional team is assembled and coordinated according to
              the scale, location and technical requirements of each project.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
